"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.focusRegion = focusRegionDecoratorFactory;
var _dcCore = require("dc-core");
var _react = _interopRequireWildcard(require("react"));
var _reactDisplayName = _interopRequireDefault(require("react-display-name"));
var _reactDom = require("react-dom");
var _FocusRegionDecorator = _interopRequireDefault(require("../style/FocusRegionDecorator.css"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * Events by the user that should cause a focus region to lose focus.
 * For example, if a user clicks on the nav bar while it's focused, the navbar
 * should lose focus.
 *
 * @type {string[]}
 */
const eventsThatBlurRegion = ['mouseup', 'keypress'];

/**
 * Creates a new decorator.
 *
 * @param {!string} id - Unique ID for focus region.
 * @param {number} [position=-1] - Position of focus region among other registered regions.
 *
 * @return {function} The decorator function
 */
function focusRegionDecoratorFactory(id, position = -1) {
  const regionId = id;
  const regionPosition = position;

  /**
   * Creates a new component which wraps the specified component as an application-level
   * focus region. When mounted, the FocusRegionProvider is acquired and used to
   * add this rendered component as a focus region. It listens for focus change events
   * from the provided FocusRegion object and modifies the display to reflect its own
   * focus state.
   *
   * When the component is unmounted, the region is removed and all listeners are
   * removed.
   *
   * @param {function|Component} WrappedComponent - component to wrap
   *
   * @return {FocusRegion} wraps the specified component
   */
  function decorator(WrappedComponent) {
    class FocusRegion extends _react.PureComponent {
      constructor(...args) {
        super(...args);
        this.state = {
          focusRegion: null,
          focusRegions: null,
          hasFocus: false,
          regionElement: null
        };
        this.handleFocusChange = event => {
          if (event === 'focus') {
            this.setState({
              hasFocus: true
            }, this.addBlurRegionListeners);
          } else if (event === 'blur') {
            this.setState({
              hasFocus: false
            }, this.removeBlurRegionListeners);
          }
        };
        this.addBlurRegionListeners = () => {
          eventsThatBlurRegion.forEach(event => {
            this.state.regionElement.addEventListener(event, this.handleUserFocusRingInteraction);
          });
        };
        this.removeBlurRegionListeners = () => {
          eventsThatBlurRegion.forEach(event => {
            this.state.regionElement.removeEventListener(event, this.handleUserFocusRingInteraction);
          });
        };
        this.handleUserFocusRingInteraction = () => {
          this.handleBlur({
            target: this.state.regionElement
          });
        };
        this.handleBlur = event => {
          const {
            regionElement
          } = this.state;
          const {
            target
          } = event;
          if (target === regionElement) {
            this.setState({
              hasFocus: false
            });
            regionElement.removeEventListener('blur', this.handleBlur);
            regionElement.removeAttribute('tabindex');
          }
        };
        this.updateRegionElement = regionElement => {
          if (regionElement) {
            const domElement = (0, _reactDom.findDOMNode)(regionElement); // get root DOM element of child element
            this.setState({
              regionElement: domElement
            });
            domElement.classList.add(_FocusRegionDecorator.default.focusRegion);
          }
        };
      }
      componentDidMount() {
        _dcCore.providers['focus-regions']().then(focusRegions => {
          const focusRegion = focusRegions.addRegion(regionId, regionPosition);
          this.setState({
            focusRegion,
            focusRegions
          }, () => focusRegion.addListener(this.handleFocusChange));
        });
      }
      componentDidUpdate() {
        const {
          hasFocus,
          regionElement
        } = this.state;
        if (!regionElement) {
          return;
        }
        if (hasFocus) {
          regionElement.addEventListener('blur', this.handleBlur);
          regionElement.setAttribute('tabindex', -1);
          // wait a frame before trying to shift focus
          window.requestAnimationFrame(() => regionElement.focus());
        } else {
          regionElement.removeAttribute('tabindex');
          regionElement.blur();
        }
      }
      componentWillUnmount() {
        const {
          focusRegion,
          focusRegions,
          regionElement
        } = this.state;
        this.setState({
          focusRegion: null,
          focusRegions: null,
          hasFocus: false,
          regionElement: null
        });

        /* eslint-disable no-unused-expressions */
        regionElement === null || regionElement === void 0 ? void 0 : regionElement.removeEventListener('blur', this.handleBlur);
        focusRegions === null || focusRegions === void 0 ? void 0 : focusRegions.removeRegion(focusRegion);
      }
      render() {
        const {
          props
        } = this;
        const {
          focusRegions
        } = this.state;
        if (!focusRegions) {
          return null;
        }
        return /*#__PURE__*/_react.default.createElement(WrappedComponent, _extends({}, props, {
          ref: this.updateRegionElement
        }));
      }
    }
    FocusRegion.WrappedComponent = WrappedComponent;
    FocusRegion.displayName = `FocusRegion(${regionId}(${(0, _reactDisplayName.default)(WrappedComponent)}))`;
    return FocusRegion;
  }
  return decorator;
}